// View model for the private dashboard's Bonuses page: one row per bonus saved in
// bi_backoffice.temp_vip_bonuses_hist, newest first. Styled like the "Issued bonuses" tab.
import { formatAmount, formatDateTimeUTC, storedAmount } from '../vip/bonus_requests/format';
import { formatMoney } from '../vip/bonus_requests/notEligible';
import { rateLine } from '../vip/bonus_requests/resultCardView';
import { STATUS_FILTERS } from '../vip/bonus_requests/issuedList';

export { STATUS_FILTERS };

const DEFAULT_STATUS = 'pending';

const STATUS = {
  pending: { label: 'Pending', variant: 'warning' },
  pushed: { label: 'Pushed', variant: 'info' },
  issued: { label: 'Issued', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

const KNOWN_STATUSES = new Set(STATUS_FILTERS.map((f) => f.key));

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const USER_ID = /^\d+$/;

// filtersFromSearch: the filters in the URL. A missing or unknown status is pending ('' = all), a
// missing or bad page is 1; from/to are UTC days and q a user ID (emails stay out of the URL).
export const filtersFromSearch = (sp) => {
  const raw = sp.get('status');
  const status = raw !== null && KNOWN_STATUSES.has(raw) ? raw : DEFAULT_STATUS;
  const page = Number.parseInt(sp.get('page'), 10);
  const date = (key) => (DATE.test(sp.get(key) || '') ? sp.get(key) : '');
  const q = sp.get('q') || '';
  return { status, page: page > 0 ? page : 1, from: date('from'), to: date('to'), q: USER_ID.test(q) ? q : '' };
};

// searchWithFilters: a copy of the URL params holding these filters; empty ones and page 1 are
// left out.
export const searchWithFilters = (sp, { status, page, from = '', to = '', q = '' }) => {
  const next = new URLSearchParams(sp);
  next.set('status', status);
  Object.entries({ from, to, q, page: page > 1 ? String(page) : '' }).forEach(([key, value]) => {
    if (value) next.set(key, value);
    else next.delete(key);
  });
  return next;
};

// playerSearchError: why the search box text is not a user ID or an email ('' when it is, or empty).
export const playerSearchError = (text) => (text && !USER_ID.test(text) && !text.includes('@')
  ? 'Enter a numeric user ID or an email address.'
  : '');

// listParams: the query of GET /api/vip-private/bonuses; search is a user ID or an email.
export const listParams = ({ status, page, from, to, search }, pageSize) => ({
  ...(status ? { status } : {}),
  ...(from ? { from } : {}),
  ...(to ? { to } : {}),
  ...(search ? { q: search } : {}),
  page,
  page_size: pageSize,
});

export const bonusRowView = (row) => {
  const changed = row.updated_by
    ? `${row.status === 'cancelled' ? 'cancelled' : 'changed'} by ${row.updated_by} · ${formatDateTimeUTC(row.updated_at)}`
    : '';
  const isUSD = row.currency === 'USD';
  return {
    id: row.id,
    userId: row.user_id,
    email: row.player_email || '',
    added: formatDateTimeUTC(row.created_at),
    addedBy: row.created_by,
    changed,
    amount: formatAmount(storedAmount(row.amount), row.currency),
    title: row.title,
    terms: `${Number(row.wagering)}x · ${row.duration_days} ${row.duration_days === 1 ? 'day' : 'days'}`,
    usd: row.amount_usd && !isUSD ? `≈ ${formatMoney(row.amount_usd, 'USD')}` : '',
    rate: row.amount_usd && !isUSD ? rateLine(row.exchange_rate, 'USD', row.currency) : null,
    status: STATUS[row.status] || { label: row.status, variant: 'secondary' },
    manualApproval: Boolean(row.manual_approve),
    comment: row.comment || '',
    canCancel: row.status === 'pending',
    bonusId: row.issued_bonus_id ?? null,
  };
};
